import { useEffect, useMemo, useState } from "react";
import {
  createAlertRule,
  createTickerFilingAlert,
  deleteAlertRule,
  loadAlertDeliveries,
  type AlertDelivery,
  type AlertRule,
  type AlertRuleType
} from "../lib/alerts-api";
import { ensureDefaultAlertRules } from "../lib/alert-bootstrap";
import {
  createNamedWatchlist,
  deleteNamedWatchlist,
  loadNamedWatchlists,
  mutateNamedWatchlistTicker,
  type NamedWatchlist
} from "../lib/watchlists-api";
import {
  createNote,
  deleteNote,
  loadNotes,
  type EntityNote
} from "../lib/notes-api";
import { formatShortDate } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { StatusBadge } from "./ui/status-badge";

type WorkspaceTab = "watchlists" | "alerts" | "history" | "notes";

interface WorkspaceDrawerProps {
  open: boolean;
  authenticated: boolean;
  activeTicker?: string | null;
  sessionEmail?: string | null;
  initialTab?: WorkspaceTab;
  alertNotice?: string | null;
  onClose: () => void;
  onAlertCreated?: (rule: AlertRule) => void;
}

function deliveryVariant(status: string): "buy" | "warning" | "destructive" | "neutral" {
  if (status === "sent") return "buy";
  if (status === "failed") return "destructive";
  if (status === "suppressed" || status === "queued") return "warning";
  return "neutral";
}

export function WorkspaceDrawer({
  open,
  authenticated,
  activeTicker = null,
  sessionEmail = null,
  initialTab = "alerts",
  alertNotice = null,
  onClose,
  onAlertCreated
}: WorkspaceDrawerProps) {
  const [tab, setTab] = useState<WorkspaceTab>(initialTab);
  const [watchlists, setWatchlists] = useState<NamedWatchlist[]>([]);
  const [alerts, setAlerts] = useState<AlertRule[]>([]);
  const [deliveries, setDeliveries] = useState<AlertDelivery[]>([]);
  const [notes, setNotes] = useState<EntityNote[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [watchlistName, setWatchlistName] = useState("");
  const [alertName, setAlertName] = useState("CEO/CFO buys");
  const [alertType, setAlertType] = useState<AlertRuleType>("ceo_cfo_buy");
  const [alertMinValue, setAlertMinValue] = useState("100000");
  const [noteBody, setNoteBody] = useState("");
  const [localNotice, setLocalNotice] = useState<string | null>(null);

  const canUse = authenticated && open;
  const notice = localNotice ?? alertNotice;

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!canUse) return;
    let cancelled = false;
    setPending(true);
    setError(null);
    Promise.all([
      loadNamedWatchlists(),
      ensureDefaultAlertRules(sessionEmail),
      loadNotes(),
      loadAlertDeliveries(40).catch(() => [] as AlertDelivery[])
    ])
      .then(([nextWatchlists, nextAlerts, nextNotes, nextDeliveries]) => {
        if (cancelled) return;
        setWatchlists(nextWatchlists);
        setAlerts(nextAlerts);
        setNotes(nextNotes);
        setDeliveries(nextDeliveries);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      })
      .finally(() => {
        if (!cancelled) setPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canUse, sessionEmail]);

  const notePreview = useMemo(() => notes.slice(0, 20), [notes]);

  if (!open) return null;

  return (
    <aside
      aria-label="Research workspace"
      className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-surface shadow-2xl"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Workspace</p>
          <h2 className="text-sm font-semibold">Watchlists, alerts, notes</h2>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </header>

      {!authenticated ? (
        <div className="p-4 text-sm text-muted-foreground">
          Sign in to manage named watchlists, alert rules, and entity notes.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 border-b border-border px-3 py-2">
            {(
              [
                ["watchlists", "Watchlists"],
                ["alerts", "Alerts"],
                ["history", "History"],
                ["notes", "Notes"]
              ] as const
            ).map(([id, label]) => (
              <Button
                key={id}
                type="button"
                size="sm"
                variant={tab === id ? "default" : "ghost"}
                onClick={() => setTab(id)}
              >
                {label}
              </Button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {error ? (
              <p className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {error}
              </p>
            ) : null}
            {notice ? (
              <p className="mb-3 rounded-md border border-accent/30 bg-accent-soft px-3 py-2 text-xs text-accent">
                {notice}
              </p>
            ) : null}
            {pending ? (
              <p className="mb-3 text-xs text-muted-foreground">Syncing workspace…</p>
            ) : null}

            {tab === "watchlists" ? (
              <section className="space-y-3">
                <form
                  className="flex gap-2"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (!watchlistName.trim()) return;
                    setPending(true);
                    setError(null);
                    try {
                      const created = await createNamedWatchlist(watchlistName.trim());
                      setWatchlists((current) => [created, ...current]);
                      setWatchlistName("");
                    } catch (submitError) {
                      setError(
                        submitError instanceof Error ? submitError.message : String(submitError)
                      );
                    } finally {
                      setPending(false);
                    }
                  }}
                >
                  <Input
                    value={watchlistName}
                    onChange={(event) => setWatchlistName(event.target.value)}
                    placeholder="Named watchlist"
                    className="h-8 text-xs"
                  />
                  <Button type="submit" size="sm" disabled={pending}>
                    Create
                  </Button>
                </form>
                <ul className="space-y-2">
                  {watchlists.map((watchlist) => (
                    <li key={watchlist.id} className="rounded-md border border-border p-3 text-xs">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium">{watchlist.name}</div>
                          <div className="text-muted-foreground">
                            {watchlist.tickers.length} tickers
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            setPending(true);
                            try {
                              await deleteNamedWatchlist(watchlist.id);
                              setWatchlists((current) =>
                                current.filter((item) => item.id !== watchlist.id)
                              );
                            } catch (deleteError) {
                              setError(
                                deleteError instanceof Error
                                  ? deleteError.message
                                  : String(deleteError)
                              );
                            } finally {
                              setPending(false);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {watchlist.tickers.map((ticker) => (
                          <StatusBadge key={ticker} variant="neutral">
                            {ticker}
                          </StatusBadge>
                        ))}
                      </div>
                      {activeTicker ? (
                        <div className="mt-2 flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              setPending(true);
                              try {
                                const updated = await mutateNamedWatchlistTicker(
                                  watchlist.id,
                                  activeTicker,
                                  "add"
                                );
                                setWatchlists((current) =>
                                  current.map((item) =>
                                    item.id === updated.id ? updated : item
                                  )
                                );
                              } catch (mutateError) {
                                setError(
                                  mutateError instanceof Error
                                    ? mutateError.message
                                    : String(mutateError)
                                );
                              } finally {
                                setPending(false);
                              }
                            }}
                          >
                            Add {activeTicker}
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {tab === "alerts" ? (
              <section className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Starter rules seed on first open (CEO/CFO buys, cluster buys, watchlist filings).
                  Morning digest evaluates enabled rules against the live export.
                </p>
                <div className="rounded-md border border-accent/30 bg-accent-soft p-3 text-[11px] leading-5 text-accent">
                  Habit loop: one-click ticker alerts from the table, then open Action queue when mail
                  fires or review History for delivery status.
                </div>

                {activeTicker ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-border p-3">
                    <div className="text-xs">
                      <div className="font-medium">Alert on {activeTicker} filings</div>
                      <div className="text-muted-foreground">
                        Creates a watchlist_filing rule scoped to this ticker.
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={pending}
                      onClick={async () => {
                        setPending(true);
                        setError(null);
                        setLocalNotice(null);
                        try {
                          const created = await createTickerFilingAlert(activeTicker);
                          setAlerts((current) => [created, ...current]);
                          setLocalNotice(`Alert rule created for ${activeTicker}.`);
                          onAlertCreated?.(created);
                        } catch (submitError) {
                          setError(
                            submitError instanceof Error
                              ? submitError.message
                              : String(submitError)
                          );
                        } finally {
                          setPending(false);
                        }
                      }}
                    >
                      Alert {activeTicker}
                    </Button>
                  </div>
                ) : null}

                <form
                  className="space-y-2 rounded-md border border-border p-3"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    setPending(true);
                    setError(null);
                    setLocalNotice(null);
                    try {
                      const minValue = alertMinValue.trim()
                        ? Number(alertMinValue)
                        : null;
                      const created = await createAlertRule({
                        name: alertName.trim() || alertType,
                        type: alertType,
                        params: {
                          minValue: Number.isFinite(minValue as number) ? minValue : null
                        }
                      });
                      setAlerts((current) => [created, ...current]);
                      setLocalNotice(`Created rule “${created.name}”.`);
                      onAlertCreated?.(created);
                    } catch (submitError) {
                      setError(
                        submitError instanceof Error ? submitError.message : String(submitError)
                      );
                    } finally {
                      setPending(false);
                    }
                  }}
                >
                  <Input
                    value={alertName}
                    onChange={(event) => setAlertName(event.target.value)}
                    placeholder="Alert name"
                    className="h-8 text-xs"
                  />
                  <label className="block text-[11px] text-muted-foreground">
                    Type
                    <select
                      className="mt-1 h-8 w-full rounded-md border border-border bg-surface-elevated px-2 text-xs"
                      value={alertType}
                      onChange={(event) => setAlertType(event.target.value as AlertRuleType)}
                    >
                      <option value="watchlist_filing">Watchlist filings</option>
                      <option value="ceo_cfo_buy">CEO/CFO informative buys</option>
                      <option value="cluster_buy">Cluster buys</option>
                    </select>
                  </label>
                  <Input
                    value={alertMinValue}
                    onChange={(event) => setAlertMinValue(event.target.value)}
                    placeholder="Min value (optional)"
                    className="h-8 text-xs"
                  />
                  <Button type="submit" size="sm" disabled={pending}>
                    Create alert rule
                  </Button>
                </form>
                <ul className="space-y-2">
                  {alerts.map((rule) => (
                    <li key={rule.id} className="rounded-md border border-border p-3 text-xs">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium">{rule.name}</div>
                          <div className="text-muted-foreground">
                            {rule.type}
                            {rule.params.minValue != null ? ` · min $${rule.params.minValue}` : ""}
                            {rule.params.tickers?.length
                              ? ` · ${rule.params.tickers.join(", ")}`
                              : ""}
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            setPending(true);
                            try {
                              await deleteAlertRule(rule.id);
                              setAlerts((current) => current.filter((item) => item.id !== rule.id));
                            } catch (deleteError) {
                              setError(
                                deleteError instanceof Error
                                  ? deleteError.message
                                  : String(deleteError)
                              );
                            } finally {
                              setPending(false);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                      <div className="mt-2">
                        <StatusBadge variant={rule.isEnabled ? "buy" : "warning"}>
                          {rule.isEnabled ? "enabled" : "disabled"}
                        </StatusBadge>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {tab === "history" ? (
              <section className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Recent delivery attempts recorded in D1 after morning evaluate/deliver runs.
                </p>
                {deliveries.length === 0 ? (
                  <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
                    No delivery rows yet. After the next weekday digest, successful and suppressed
                    sends will appear here.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {deliveries.map((delivery) => (
                      <li key={delivery.id} className="rounded-md border border-border p-3 text-xs">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {delivery.ruleName || delivery.ruleType || "Alert"}
                            </div>
                            <div className="text-muted-foreground">
                              {delivery.sentAt
                                ? formatShortDate(delivery.sentAt.slice(0, 10))
                                : formatShortDate(delivery.createdAt.slice(0, 10))}
                              {" · "}
                              {delivery.channel}
                            </div>
                            {delivery.error ? (
                              <div className="mt-1 text-[11px] text-warning">{delivery.error}</div>
                            ) : null}
                          </div>
                          <StatusBadge variant={deliveryVariant(delivery.status)}>
                            {delivery.status}
                          </StatusBadge>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}

            {tab === "notes" ? (
              <section className="space-y-3">
                <form
                  className="space-y-2 rounded-md border border-border p-3"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (!activeTicker || !noteBody.trim()) return;
                    setPending(true);
                    setError(null);
                    try {
                      const created = await createNote({
                        entityType: "ticker",
                        entityId: activeTicker,
                        body: noteBody.trim()
                      });
                      setNotes((current) => [created, ...current]);
                      setNoteBody("");
                    } catch (submitError) {
                      setError(
                        submitError instanceof Error ? submitError.message : String(submitError)
                      );
                    } finally {
                      setPending(false);
                    }
                  }}
                >
                  <p className="text-[11px] text-muted-foreground">
                    Active ticker: {activeTicker ?? "select a row first"}
                  </p>
                  <textarea
                    value={noteBody}
                    onChange={(event) => setNoteBody(event.target.value)}
                    className="min-h-[88px] w-full rounded-md border border-border bg-surface-elevated p-2 text-xs"
                    placeholder="Research note"
                  />
                  <Button type="submit" size="sm" disabled={pending || !activeTicker}>
                    Save note
                  </Button>
                </form>
                <ul className="space-y-2">
                  {notePreview.map((note) => (
                    <li key={note.id} className="rounded-md border border-border p-3 text-xs">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium">
                            {note.entityType}:{note.entityId}
                          </div>
                          <p className="mt-1 text-muted-foreground">{note.body}</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            setPending(true);
                            try {
                              await deleteNote(note.id);
                              setNotes((current) => current.filter((item) => item.id !== note.id));
                            } catch (deleteError) {
                              setError(
                                deleteError instanceof Error
                                  ? deleteError.message
                                  : String(deleteError)
                              );
                            } finally {
                              setPending(false);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </>
      )}
    </aside>
  );
}
